import { type SxProps, type Theme } from '@mui/material';
import { DataGrid, DataGridProps } from '@mui/x-data-grid';

import CustomNoRowsOverlay from './CustomNoRowsOverlay';
import CustomPagination from './CustomPagination';
import CustomToolbar from './CustomToolbar';

interface CustomDataGridProps extends DataGridProps {
    pointer?: boolean;
    toolbar?: boolean;
}

export default function CustomDatagrid(props: Readonly<CustomDataGridProps>) {
    const { getRowClassName, initialState, onRowClick, pointer, slotProps, slots, sx, toolbar, ...rest } = props;
    const showPointer = pointer || Boolean(onRowClick);
    const baseSx = {
        '--DataGrid-overlayHeight': '300px',
        '& .MuiDataGrid-cell:focus': {
            outline: 'none',
        },
        '& .MuiDataGrid-row:hover:not(.pointer-row)': {
            cursor: 'default',
        },
        '& .onlyView-row': {
            backgroundColor: '#f0f0f0',
            color: '-moz-initial',
            cursor: 'default',
        },
        '& .onlyView-row:hover': {
            backgroundColor: '#f0f0f0',
            cursor: 'default',
        },
        '& .pointer-row, & .pointer-row .MuiDataGrid-cell': {
            cursor: 'pointer',
        },
    };

    const mergedSx = (sx ? [baseSx, sx] : [baseSx]) as SxProps<Theme>;
    const mergedInitialState = {
        ...initialState,
        pagination: {
            ...initialState?.pagination,
            paginationModel: {
                page: 0,
                pageSize: 25,
                ...initialState?.pagination?.paginationModel,
            },
        },
    };
    const mergedSlotProps = {
        ...slotProps,
        toolbar: toolbar
            ? {
                  showQuickFilter: true,
                  ...slotProps?.toolbar,
              }
            : slotProps?.toolbar,
    };
    const mergedSlots = {
        ...slots,
        noRowsOverlay: slots?.noRowsOverlay ?? CustomNoRowsOverlay,
        pagination: slots?.pagination ?? CustomPagination,
        toolbar: toolbar ? (slots?.toolbar ?? CustomToolbar) : slots?.toolbar,
    };

    return (
        <DataGrid
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            {...rest}
            getRowClassName={(params) => {
                const existingClassName = getRowClassName?.(params) ?? '';

                if (!showPointer) {
                    return existingClassName;
                }

                return [existingClassName, 'pointer-row'].filter(Boolean).join(' ');
            }}
            initialState={mergedInitialState}
            isCellEditable={() => false}
            onRowClick={onRowClick}
            slotProps={mergedSlotProps}
            slots={mergedSlots}
            sx={mergedSx}
        />
    );
}
